"use client";

import { useAtomSet, useAtomValue } from "@effect/atom-react";
import {
  COMPUTER_AUTOMATION_OPERATIONS,
  type ComputerAutomationHost as ComputerAutomationHostState,
  type ComputerAutomationRequest,
  type EnvironmentId,
} from "@praxa/contracts";
import { Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isElectron } from "~/env";
import { computerAutomationEnvironment } from "~/state/computerAutomation";
import { useEnvironments } from "~/state/environments";
import { useAtomCommand } from "~/state/use-atom-command";

import { createComputerAutomationClientId } from "./computerAutomationClientId";
import {
  ComputerAutomationHostFailure,
  createComputerAutomationRequestConsumerAtom,
} from "./computerAutomationRequestConsumer";
import { computerBridge } from "./computerBridge";
import { ComputerControlBanner } from "./ComputerControlBanner";

export function ComputerAutomationHosts() {
  const { environments } = useEnvironments();
  if (!isElectron || !computerBridge) return null;
  return (
    <>
      <ComputerControlBanner />
      {environments.map((environment) => (
        <ComputerAutomationHost
          environmentId={environment.environmentId}
          key={environment.environmentId}
        />
      ))}
    </>
  );
}

function ComputerAutomationHost({ environmentId }: { readonly environmentId: EnvironmentId }) {
  const [clientId] = useState(createComputerAutomationClientId);
  const host = useMemo<ComputerAutomationHostState>(
    () => ({
      clientId,
      environmentId,
      supportedOperations: [...COMPUTER_AUTOMATION_OPERATIONS],
    }),
    [clientId, environmentId],
  );
  const requestsAtom = computerAutomationEnvironment.requests({
    environmentId,
    input: host,
  });
  const respond = useAtomCommand(
    computerAutomationEnvironment.respond,
    "computer automation response",
  );
  const focusHost = useAtomCommand(
    computerAutomationEnvironment.focusHost,
    "computer automation host focus",
  );
  const [connectionAtom] = useState(() => Atom.make<string | null>(null));
  const connectionId = useAtomValue(connectionAtom);

  const handleRequest = useCallback(async (request: ComputerAutomationRequest) => {
    const bridge = computerBridge;
    if (!bridge) {
      throw new ComputerAutomationHostFailure({
        _tag: "ComputerAutomationRemoteUnavailableError",
        message: "Computer control is unavailable outside the desktop app.",
      });
    }
    const outcome = await bridge.execute(request);
    if (!outcome.ok) throw new ComputerAutomationHostFailure(outcome.error);
    return outcome.result;
  }, []);
  const [requestHandlerAtom] = useState(() => Atom.make({ handle: handleRequest }));
  const setRequestHandler = useAtomSet(requestHandlerAtom);
  useEffect(() => {
    setRequestHandler({ handle: handleRequest });
  }, [handleRequest, setRequestHandler]);

  const consumerAtom = useMemo(
    () =>
      createComputerAutomationRequestConsumerAtom({
        requestsAtom,
        clientId,
        connectionAtom,
        requestHandlerAtom,
        respond: (response) => respond({ environmentId, input: response }),
        label: `computer:automation-host:${environmentId}:${clientId}`,
      }),
    [clientId, connectionAtom, environmentId, requestHandlerAtom, requestsAtom, respond],
  );
  useAtomValue(consumerAtom);

  useEffect(() => {
    const report = () => {
      if (!connectionId) return;
      void focusHost({
        environmentId,
        input: {
          clientId,
          environmentId,
          connectionId,
          focused: document.hasFocus(),
        },
      });
    };
    report();
    window.addEventListener("focus", report);
    window.addEventListener("blur", report);
    return () => {
      window.removeEventListener("focus", report);
      window.removeEventListener("blur", report);
      // A disconnected host must not leave global input authority behind.
      void computerBridge?.releaseControl();
    };
  }, [clientId, connectionId, environmentId, focusHost]);

  return null;
}
